import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosRequestConfig } from 'axios';
import { BasicStateTypes } from '../types/types';
import { ADMIN_BASE_URL, HEADERS } from '../utils/config';
import { AppSnackbar } from '../hooks/appSnackbar';

const initialState: BasicStateTypes = {
  loading: false,
  data: null,
  error: null,
};

const buildUrl = (slug: string | null, query: string | null, pageNo: string | null, keyword: string | null) => {
  const url = new URL(`${ADMIN_BASE_URL}/categories${slug ? `/${slug}` : ''}`);
  if (query) {
    url.searchParams.append('name', query);
  } else if (keyword) {
    url.searchParams.append('name', keyword);
  }
  url.searchParams.append('page', pageNo || '1');
  return url.toString();
};

export const categoryCrud = createAsyncThunk(
  'category/categoryCrud',
  async ({ method, data, slug, query }: { method: string, data: any, slug: string | null, query: string | null }, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const pageNo = searchParams.get("page");
      const keyword = searchParams.get("keyword");
      const url = buildUrl(slug, query, pageNo, keyword);
      
      const config: AxiosRequestConfig = {
        method,
        url,
        headers: await HEADERS(),
        data,
      };
      
      const response = await axios(config);
      
      let successMessage = '';
      switch (method) {
        case 'POST':
          successMessage = 'Successfully created';
          break;
        case 'PUT':
        case 'PATCH':
          successMessage = 'Successfully updated';
          break;
        case 'DELETE':
          successMessage = 'Successfully deleted';
          break;
        default:
          break;
      }
      
      if (successMessage) {
        AppSnackbar({ type: 'success', message: successMessage });
      }
      
      return response.data;
    } catch (error: any) {
      let errorMessage = error.response?.data?.message || error.message || 'Network error!';
      AppSnackbar({ type: 'error', message: errorMessage });
      return rejectWithValue(errorMessage);
    }
  }
);

export const toggleActive = createAsyncThunk(
  'category/toggleActive',
  async ({ slug, data }: { slug: string | null, data: any }, { rejectWithValue }) => {
    try {
      const url = `${ADMIN_BASE_URL}/categories/status/${slug}`;
      const config: AxiosRequestConfig = {
        method: 'PATCH',
        url,
        headers: await HEADERS(),
        data,
      };      
      const response = await axios(config);
      AppSnackbar({ type: 'success', message: 'Successfully updated' });
      return response.data;
    } catch (error: any) {
      let errorMessage = error.response?.data?.message || error.message || 'Network error!';
      AppSnackbar({ type: 'error', message: errorMessage });
      return rejectWithValue(errorMessage);
    }
  }
);

export const categorySlice = createSlice({
  name: 'category',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(categoryCrud.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(categoryCrud.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(categoryCrud.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default categorySlice.reducer;
